import { ArchiveFilterValue } from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import { useAppSelector } from 'config/hooks';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
    doActivateTeam,
    doAddTeamMember,
    doDeactivateTeam,
    doGetTeamById,
    doGetTeamInvoicesCount,
    doGetTeamManifestsCount,
    doGetTeamJobsCount,
    doGetTeams,
    doRemoveTeamMember,
    doSetArchivationStatusFilter,
    doUpdateTeam,
} from 'store/teams/actions';
import {
    selectAllTeams,
    selectArchivationStatusFilter,
    selectSelectedTeamId,
    selectTeamInvoicesCount,
    selectTeamManifestsCount,
    selectTeamJobsCount,
    selectTeamsError,
    selectTeamsLoading,
    selectTeamsResult,
} from 'store/teams/selectors';
import { AddTeamMemberPayload, CreateUpdateTeamPayload } from 'store/teams/types';

const useTeams = (teamId?: string) => {
    const dispatch = useDispatch();

    const teamsResult = useAppSelector(selectTeamsResult);
    const teams = useAppSelector(selectAllTeams);
    const team = useAppSelector((s) => (teamId ? s.teams.entities[teamId] : undefined));
    const entities = useAppSelector((s) => s.teams.entities);
    const isLoading = useAppSelector(selectTeamsLoading);
    const error = useAppSelector(selectTeamsError);
    const selectedTeamId = useAppSelector(selectSelectedTeamId);
    const archivationStatus = useAppSelector(selectArchivationStatusFilter);
    const invoicesCount = useAppSelector(selectTeamInvoicesCount(teamId));
    const manifestsCount = useAppSelector(selectTeamManifestsCount(teamId));
    const jobsCount = useAppSelector(selectTeamJobsCount(teamId));

    const listTeams = useCallback((params?: any) => dispatch(doGetTeams(params)), [dispatch]);
    const getTeamById = useCallback((teamId: string) => dispatch(doGetTeamById(teamId)), [dispatch]);
    const activateTeam = useCallback((teamId: string) => dispatch(doActivateTeam(teamId)), [dispatch]);
    const deactivateTeam = useCallback((teamId: string) => dispatch(doDeactivateTeam(teamId)), [dispatch]);
    const updateTeam = useCallback(
        (teamId: string, payload: CreateUpdateTeamPayload) => dispatch(doUpdateTeam(teamId, payload)),
        [dispatch]
    );
    const addTeamMember = useCallback(
        (payload: AddTeamMemberPayload) => dispatch(doAddTeamMember(payload)),
        [dispatch]
    );
    const removeTeamMember = useCallback(
        (teamId: string, individualId: string) => dispatch(doRemoveTeamMember(teamId, individualId)),
        [dispatch]
    );
    const setArchivationStatus = useCallback(
        (status: ArchiveFilterValue) => dispatch(doSetArchivationStatusFilter(status)),
        [dispatch]
    );
    const getTeamInvoicesCount = useCallback((teamId: string) => dispatch(doGetTeamInvoicesCount(teamId)), [dispatch]);
    const getTeamManifestsCount = useCallback(
        (teamId: string) => dispatch(doGetTeamManifestsCount(teamId)),
        [dispatch]
    );
    const getTeamJobsCount = useCallback((teamId: string) => dispatch(doGetTeamJobsCount(teamId)), [dispatch]);

    return {
        team,
        teams,
        entities,
        isLoading,
        error,
        selectedTeamId,
        teamsResult,
        archivationStatus,
        setArchivationStatus,
        invoicesCount,
        manifestsCount,
        jobsCount,
        getTeamInvoicesCount,
        getTeamManifestsCount,
        getTeamJobsCount,
        listTeams,
        getTeamById,
        activateTeam,
        deactivateTeam,
        addTeamMember,
        removeTeamMember,
        updateTeam,
    };
};

export default useTeams;
